import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTable, useExpanded, useColumnOrder } from "react-table";
import { FormattedMessage, useIntl } from "react-intl";

import DataTable from "components/v2/Table/DataTable";
import Badge from "components/v2/Badge/Badge";
import FormSection from "components/v2/FormSection/FormSection";
import FormHeader from "components/v2/FormSection/components/FormHeader/FormHeader";
import { IGoodItem } from "pages/provider/QuotationDetail/QuotationType";
import Alert from "components/v2/Alert/Alert";
import Button from "components/v2/Button/Button";
import { useGetPackingListConfig } from "api/queries/location/useGetPackingListConfig";
import usePermissions from "hooks/usePermissions";
import getPackingListColumn from "./PackingListColumn";
import SubLists from "./SubLists";
import QuickRequestDrawer from "../../../QuickRequestDrawer/QuickRequestDrawer";
import PackingListDetailsDrawer from "./PackingListDetailsDrawer";

interface IPackingListProps {
  isLoading: boolean;
  data: any;
}

function PackingList({ data, isLoading }: IPackingListProps) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { unit_system_preferences } = JSON.parse(permissions ?? "{}");

  const { watch } = useFormContext();
  const location = watch("organizational.location");
  const [isDangerousGood, setIsDangerousGood] = useState<string[]>([]);
  const [isTemperatureControlRequired, setIsTemperatureControlRequired] = useState<string[]>([]);
  const [goodsConfirmationType, setGoodsConfirmationType] = useState<string[]>([]);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [quickRequestReference, setQuickRequestReference] = useState<string | null>(null);
  const [showPackingListDetails, setShowPackingListDetails] = useState<boolean>(false);

  const { data: packingListConfig }: any = useGetPackingListConfig(location, {
    enabled: !!location,
    refetchOnMount: false,
  });

  // To check if any of the packing list items have Dangerous Goods or are Temeprature controlled
  useEffect(() => {
    if (data?.length > 0) {
      let temperatureItems: string[] = [];
      let dangerousGood: string[] = [];
      let finalizedGood: string[] = [];
      data?.forEach((value: any) => {
        const { organization } = value?.data || [];
        if (organization?.goods_confirmation_type !== "Finalized")
          finalizedGood = [...finalizedGood, organization?.quick_request_reference];
        organization?.good_items?.forEach((badgeValue: IGoodItem) => {
          if (badgeValue?.temperature_control_required)
            temperatureItems = [...temperatureItems, organization?.quick_request_reference];
          if (badgeValue?.is_dangerous_good) dangerousGood = [...dangerousGood, organization?.quick_request_reference];
        });
      });
      setIsTemperatureControlRequired(temperatureItems);
      setIsDangerousGood(dangerousGood);
      setGoodsConfirmationType(finalizedGood);
    }
  }, [data]);

  // Check for unfinalized packing lists and update showAlert state
  useEffect(() => {
    const isUnfinalised = goodsConfirmationType?.length > 0 && packingListConfig?.good_list_estimation_enabled;
    setShowAlert(isUnfinalised);
  }, [goodsConfirmationType, location, packingListConfig]);

  const fallbackMessage = intl.formatMessage({
    id: "new-request.packing-list.noDataFound",
    defaultMessage: "No Data Found!",
  });

  const handleRowClick = (row: any) => row.toggleRowExpanded();
  const handleQRClick = (e: any, referenceNumber: string) => {
    e.stopPropagation();
    setQuickRequestReference(referenceNumber);
  };

  const columns = useMemo(
    () =>
      getPackingListColumn(
        intl,
        isTemperatureControlRequired,
        isDangerousGood,
        goodsConfirmationType,
        handleQRClick,
        packingListConfig,
        unit_system_preferences,
      ),
    [intl, isTemperatureControlRequired, isDangerousGood, packingListConfig, unit_system_preferences],
  );

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useExpanded,
    useColumnOrder,
  );

  const closeDrawer = () => {
    setShowPackingListDetails(false);
  };

  return (
    <div className="schedule-container radius-2xl mt-3xl">
      <QuickRequestDrawer
        isOpen={quickRequestReference}
        handleClose={() => setQuickRequestReference(null)}
        QuickRequestReference={quickRequestReference}
      />
      <PackingListDetailsDrawer isOpen={showPackingListDetails} handleClose={closeDrawer} data={data} />
      <FormSection>
        <FormHeader
          title="Packing list"
          button={
            <Button
              onClick={() => setShowPackingListDetails(true)}
              label={intl.formatMessage({
                id: "new-request.form.viewPackingListDetails",
                defaultMessage: "View packing list details",
              })}
              variant="link-color"
            />
          }
          isAlert={showAlert}
        />
        <div className="form-section-content">
          {showAlert && (
            <Alert
              variant="warning"
              label={intl.formatMessage({
                id: "new-request.packing-list.unfinalisedPackingListsLabel",
                defaultMessage: "Your shipment contains orders with unfinalised packing lists.",
              })}
            >
              <FormattedMessage
                id="new-request.packing-list.unfinalisedPackingListsMessage"
                defaultMessage="You will not be able to make any changes to the packing lists after submitting this quote request."
              />
            </Alert>
          )}

          <div className={`text-sm text-secondary-v2 ${showAlert ? "shipment-handling" : ""}`}>
            <FormattedMessage
              id="new-request.packing-list.shipmentHandlingRequirement"
              defaultMessage="Shipment handling requirement"
            />
          </div>
          <div className="d-flex flex-wrap mt-2 text-xs">
            {isTemperatureControlRequired?.length > 0 && (
              <Badge
                color="indigo"
                type="category-badge"
                label={intl.formatMessage({
                  id: "new-request.packing-list.temperatureControlled",
                  defaultMessage: "Temperature controlled",
                })}
              />
            )}
            {isDangerousGood?.length > 0 && (
              <div className="ml-2">
                <Badge
                  color="secondary-orange"
                  type="category-badge"
                  label={intl.formatMessage({ id: "general.dangerous", defaultMessage: "Dangerous" })}
                />
              </div>
            )}
          </div>
          <div className="mt-4">
            <DataTable
              tableInstance={tableInstance}
              RowSubComponent={SubLists}
              isLoading={isLoading}
              customNoResults={fallbackMessage}
              onRowClick={handleRowClick}
            />
          </div>
        </div>
      </FormSection>
    </div>
  );
}

export default PackingList;
